/**
 * La copertura della commessa (GI-239): che cosa della linea non e' coperto,
 * e che cosa farne prima della consegna.
 *
 * **Una domanda sola.** Il modulo di prima rispondeva a quattro: gli elementi
 * scoperti, i fotogrammi da guardare, l'anomalia trovata a mano, la mappa dei
 * punti. Tre di queste vivono oggi altrove — Foto porta i fotogrammi e la
 * dichiarazione a mano, la Mappa porta i punti — e qui resta la prima: quali
 * elementi nessuno ha guardato, e come si spiega un buco che non si chiude.
 *
 * **Due colonne, la pagina non scorre.** A sinistra il conto che blocca, i
 * filtri per stato e la tabella degli elementi, che scorre dentro la sua
 * colonna; a destra l'elemento scelto con la dichiarazione di buco — motivo
 * dall'elenco chiuso, nota facoltativa — o il suo ritiro, e le uscite verso la
 * scheda dell'elemento e le foto che lo inquadrano.
 *
 * **La scrittura passa dalla casella d'uscita (GI-172).** Una dichiarazione e'
 * un intento `not_inspected` sulla sessione di verifica, con la stessa
 * idempotenza e la stessa riconciliazione dei verdetti. Offline si accoda, e
 * lo si dice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LineMap } from '../carta/LineMap'
import { decimal } from '../format'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { ServiceError } from '../api'
import * as coverage from '../coverage'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { outbox } from '../outbox'
import type { Declaration, IntentState, Rejected } from '../outbox'
import { subjectOf } from '../reviewProtocol'
import { HEARTBEAT_MS, heartbeat, openSession } from '../reviewSessions'
import { parseReviewSessionId } from '../router'
import type { CoverageElement, CoverageMap, CoverageStatus, NotInspectedReason } from '../types'
import './coverage.css'

export const COPERTURA = 'modulo-copertura'

/** Uno stato con glifo, parola e significato: mai uno dei tre da solo. */
interface Badge {
  glyph: string
  name: MessageKey
  tone: string
  meaning: MessageKey
}

const COPERTURE: Record<CoverageStatus, Badge> = {
  covered: { glyph: '●', name: 'coverage.statusCovered', tone: 'sev-0', meaning: 'coverage.meaningCovered' },
  declared: { glyph: '◆', name: 'coverage.statusDeclared', tone: 'sev-2', meaning: 'coverage.meaningDeclared' },
  gap: { glyph: '▲', name: 'coverage.statusGap', tone: 'sev-4', meaning: 'coverage.meaningGap' },
}

const CONSEGNE: Record<IntentState, Badge> = {
  decision_pending: { glyph: '◇', name: 'coverage.deliveryPending', tone: 'ref', meaning: 'coverage.meaningPending' },
  applied: { glyph: '✓', name: 'coverage.deliveryApplied', tone: 'sev-0', meaning: 'coverage.meaningApplied' },
  withdrawn: { glyph: '↺', name: 'coverage.deliveryWithdrawn', tone: 'sev-1', meaning: 'coverage.meaningWithdrawn' },
  rejected: { glyph: '■', name: 'coverage.deliveryRejected', tone: 'sev-4', meaning: 'coverage.meaningRejected' },
  stale: { glyph: '⟳', name: 'coverage.deliveryStale', tone: 'sev-3', meaning: 'coverage.meaningStale' },
}

/** La legenda: gli stessi segni delle tabelle, col loro significato per
 *  esteso. Il significato stava solo nel `title=` del segno — per chi tocca,
 *  tabula o ascolta non c'era (GI-281). Chiusa per difetto: chi la sa la salta. */
function Legend(): JSX.Element {
  const t = useT()
  const groups: [MessageKey, Badge[]][] = [
    ['coverage.colStatus', Object.values(COPERTURE)],
    ['coverage.deliveryState', Object.values(CONSEGNE)],
  ]
  return (
    <details className="coverage-legend">
      <summary>{t('coverage.legend')}</summary>
      {groups.map(([title, badges]) => (
        <dl key={title}>
          <dt className="tenue">{t(title)}</dt>
          {badges.map((badge) => (
            <dd key={badge.name}>
              <span className="coverage-mark" style={{ color: `var(--${badge.tone})` }}>
                <span className="glifo" aria-hidden="true">
                  {badge.glyph}
                </span>
                {t(badge.name)}
              </span>
              <span className="tenue"> — {t(badge.meaning)}</span>
            </dd>
          ))}
        </dl>
      ))}
    </details>
  )
}

function Mark({ badge }: { badge: Badge }): JSX.Element {
  const t = useT()
  return (
    <span className="coverage-mark" style={{ color: `var(--${badge.tone})` }}>
      <span className="glifo" aria-hidden="true">
        {badge.glyph}
      </span>
      {t(badge.name)}
      <span className="solo-lettori"> · {t(badge.meaning)}</span>
    </span>
  )
}

const STATUSES: { id: CoverageStatus | null; key: MessageKey }[] = [
  { id: null, key: 'coverage.filterAll' },
  { id: 'gap', key: 'coverage.statusGap' },
  { id: 'declared', key: 'coverage.statusDeclared' },
  { id: 'covered', key: 'coverage.statusCovered' },
]

export function CoverageView({ inspectionId }: { inspectionId: string }): JSX.Element {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const chosenId = params.get('element')
  const sessionId = parseReviewSessionId(location.search)

  const [map, setMap] = useState<CoverageMap | null>(null)
  const [reasons, setReasons] = useState<NotInspectedReason[]>([])
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [declarations, setDeclarations] = useState<Map<string, IntentState>>(new Map())
  const [unsent, setUnsent] = useState(0)
  const [refused, setRefused] = useState<Rejected[]>([])
  const [said, setSaid] = useState<string | null>(null)
  const [session, setSession] = useState<string | null>(sessionId)
  const [sessionRefused, setSessionRefused] = useState<ServiceError | null>(null)
  const [sessionEpoch, setSessionEpoch] = useState(0)
  const [online, setOnline] = useState(window.navigator.onLine)
  const [status, setStatus] = useState<CoverageStatus | null>(null)
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  const now = useRef<{ sessionId: string; reviewEpoch: number } | null>(null)
  useEffect(() => {
    now.current = session === null ? null : { sessionId: session, reviewEpoch: sessionEpoch }
  }, [session, sessionEpoch])

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  // Una sola casella d'uscita per tutto il modulo: crearne una a ogni ridisegno
  // perderebbe le dichiarazioni ancora da consegnare.
  const posta = useMemo(
    () =>
      outbox({
        onChange: setUnsent,
        onRejected: (one) => setRefused((were) => [...were, one]),
        session: () => now.current,
      }),
    [],
  )

  useEffect(() => {
    let alive = true
    const onFailure = (error: unknown): void => {
      if (alive && error instanceof ServiceError) setFailure(error)
    }
    coverage
      .coverageMap(inspectionId)
      .then((found) => alive && setMap(found))
      .catch(onFailure)
    coverage
      .notInspectedReasons(inspectionId)
      .then((found) => {
        if (!alive) return
        setReasons(found)
        setReason((was) => (was === '' ? (found[0]?.code ?? '') : was))
      })
      .catch(onFailure)
    return () => {
      alive = false
    }
  }, [inspectionId, unsent])

  useEffect(() => {
    let alive = true
    void posta.declarations().then((found) => alive && setDeclarations(found))
    return () => {
      alive = false
    }
  }, [posta, unsent, refused])

  // La sessione si apre da qui, con lo stesso gesto idempotente del banco: un
  // modulo che mostra comandi spenti senza dire perche' e' un vicolo cieco.
  useEffect(() => {
    if (session !== null || !online) return
    let alive = true
    openSession(inspectionId).then(
      (opened) => {
        if (!alive) return
        setSession(opened.review_session_id)
        setSessionEpoch(opened.review_epoch)
        setSessionRefused(null)
      },
      (error: unknown) => {
        if (!alive) return
        setSessionRefused(error instanceof ServiceError ? error : null)
      },
    )
    return () => {
      alive = false
    }
  }, [inspectionId, session, online])

  useEffect(() => {
    if (session === null) return
    const timer = setInterval(() => {
      heartbeat(session).then(
        (alive) => setSessionEpoch(alive.review_epoch),
        () => undefined,
      )
    }, HEARTBEAT_MS)
    return () => clearInterval(timer)
  }, [session])

  useEffect(() => {
    void posta.flush()
    const again = (): void => void posta.flush()
    window.addEventListener('online', again)
    return () => window.removeEventListener('online', again)
  }, [posta])

  /* L'elemento scelto sta nell'indirizzo, per identita': la mappa e la scheda
   * dell'elemento mandano qui con `?element=`, e un collegamento incollato
   * riapre lo stesso soggetto. */
  const choose = useCallback(
    (elementId: string | null): void => {
      const query = new URLSearchParams(location.search)
      if (elementId === null) query.delete('element')
      else query.set('element', elementId)
      navigate({ pathname: location.pathname, search: `?${query.toString()}` }, { replace: true })
    },
    [location.pathname, location.search, navigate],
  )

  const declare = useCallback(
    (declaration: Declaration, message: string): void => {
      if (now.current === null) {
        setSaid(t('coverage.noSession'))
        return
      }
      posta.add(declaration)
      setSaid(message)
    },
    [posta, t],
  )

  function declareGap(element: CoverageElement, declared: boolean): void {
    declare(
      {
        kind: 'not_inspected',
        elementId: element.element_id,
        reasonCode: declared ? reason : null,
        note: note.trim() === '' ? undefined : note.trim(),
        expectedCandidateVersion: element.declaration_sequence,
      },
      declared
        ? t('coverage.gapDeclared', { id: element.external_element_id })
        : t('coverage.gapWithdrawn', { id: element.external_element_id }),
    )
    if (declared) setNote('')
  }

  const elements = map?.elements ?? []
  const needle = typed.trim()
  const shown = elements.filter(
    (one) =>
      (status === null || one.status === status) &&
      (needle === '' || String(one.external_element_id).includes(needle)),
  )
  const chosen = elements.find((one) => one.element_id === chosenId) ?? null
  const pendingOf = (one: CoverageElement): IntentState | undefined =>
    declarations.get(subjectOf({ kind: 'not_inspected', elementId: one.element_id }))
  const blocking = map === null ? 0 : map.gaps
  const base = `/inspections/${inspectionId}`

  const headline =
    map === null
      ? t('coverage.reading')
      : blocking === 0
        ? t('coverage.noneBlocking', { total: map.total })
        : blocking === 1
          ? t('coverage.blockingOne', { n: blocking, total: map.total })
          : t('coverage.blockingMany', { n: blocking, total: map.total })

  return (
    <div className="coverage-view" id={COPERTURA}>
      <section className="coverage-main" aria-label={t('coverage.list')}>
        <div className="coverage-head" aria-label={t('coverage.deliveryState')}>
          <p role="status" className="coverage-headline">
            {headline}
          </p>
          <p role="status" className="tenue">
            {online ? t('chrome.networkOn') : t('coverage.offline')} ·{' '}
            {unsent === 0 ? t('coverage.nothingToSend') : t('coverage.toSend', { n: unsent })} ·{' '}
            {session === null ? t('coverage.noSessionShort') : t('coverage.sessionOpen')}
            {session === null && sessionRefused !== null && (
              <> · {explainService(t, sessionRefused.code, sessionRefused.message)}</>
            )}
          </p>
          {said !== null && <p role="status">{said}</p>}
          {refused.map((one, index) => (
            // Persistente e non un avviso che sparisce: e' l'unica traccia che
            // quella dichiarazione non e' passata.
            <p role="alert" key={`${one.code ?? 'rifiuto'}-${index}`}>
              {one.message}
            </p>
          ))}
          {failure !== null && (
            <p role="alert">
              {failure.message}
              {failure.requestId !== null && <> · {t('pool.requestId', { id: failure.requestId })}</>}
            </p>
          )}
        </div>

        <div className="coverage-bar">
          <div className="coverage-statuses" role="group" aria-label={t('coverage.colStatus')}>
            {STATUSES.map((one) => {
              const count =
                map === null
                  ? 0
                  : one.id === null
                    ? map.total
                    : one.id === 'gap'
                      ? map.gaps
                      : one.id === 'declared'
                        ? map.declared
                        : map.covered
              return (
                <button key={one.key} type="button" aria-pressed={status === one.id} onClick={() => setStatus(one.id)}>
                  {t(one.key)} <span className="tenue t-num">{count}</span>
                </button>
              )
            })}
          </div>
          <label className="coverage-search">
            <span className="solo-lettori">{t('coverage.search')}</span>
            <input
              type="search"
              inputMode="numeric"
              placeholder={t('coverage.search')}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
        </div>

        <div className="coverage-scroll">
          <table className="coverage-table">
            <thead>
              <tr>
                <th>{t('coverage.colElement')}</th>
                <th>{t('coverage.colType')}</th>
                <th>{t('coverage.colStatus')}</th>
                <th>{t('coverage.colFrames')}</th>
                <th>{t('coverage.colDeclaration')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((one) => {
                const pending = pendingOf(one)
                return (
                  <tr
                    key={one.element_id}
                    className={one.element_id === chosenId ? 'on' : undefined}
                    aria-selected={one.element_id === chosenId}
                    onClick={() => choose(one.element_id)}
                  >
                    <td className="t-num">{one.external_element_id}</td>
                    <td>{one.element_type}</td>
                    <td>
                      <Mark badge={COPERTURE[one.status]} />
                    </td>
                    <td className="t-num">
                      {t('coverage.framesReviewed', {
                        frames: one.frames,
                        reviewed: one.reviewed_frames,
                        findings: one.findings,
                      })}
                    </td>
                    <td>
                      {one.not_inspected_reason !== null && <span>{one.not_inspected_reason}</span>}
                      {pending !== undefined && <Mark badge={CONSEGNE[pending]} />}
                      {one.not_inspected_reason === null && pending === undefined && (
                        <span className="tenue">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {map !== null && shown.length === 0 && (
            <p className="tenue coverage-empty">
              {elements.length === 0 ? t('coverage.noElements') : t('coverage.noElementsShow')}
            </p>
          )}
        </div>
        {/* Sotto la tabella, nella colonna che la contiene: in fondo alla
            colonna di destra stava sotto la mappa, fuori dallo schermo. */}
        <Legend />
      </section>

      <aside className="coverage-side" aria-label={t('coverage.gapTitle')}>
        {chosen === null ? (
          <div className="coverage-overview">
            <h2 className="coverage-title">{t('coverage.overview')}</h2>
            {map !== null && (
              <dl className="coverage-facts">
                <dt>{t('coverage.colStatus')}</dt>
                <dd className="t-num">
                  {decimal(map.covered)} {t('coverage.statusCovered')} · {decimal(map.declared)} {t('coverage.statusDeclared')} ·{' '}
                  {decimal(map.gaps)} {t('coverage.statusGap')}
                </dd>
                <dt>{t('coverage.colFrames')}</dt>
                <dd className="t-num">
                  {t('coverage.framesLine', {
                    frames: decimal(elements.reduce((sum, one) => sum + one.frames, 0)),
                    reviewed: decimal(elements.reduce((sum, one) => sum + one.reviewed_frames, 0)),
                    findings: decimal(elements.reduce((sum, one) => sum + one.findings, 0)),
                  })}
                </dd>
              </dl>
            )}
            <div className="coverage-minimap">
              <LineMap inspectionId={inspectionId} bare onElement={choose} />
            </div>
            <p className="tenue">{t('coverage.pickHint')}</p>
          </div>
        ) : (
          <>
            <h2 className="coverage-title t-num">
              {t('coverage.elementLine', { id: chosen.external_element_id, type: chosen.element_type })}
            </h2>
            <p>
              <Mark badge={COPERTURE[chosen.status]} />
              {pendingOf(chosen) !== undefined && (
                <>
                  {' '}
                  <Mark badge={CONSEGNE[pendingOf(chosen) as IntentState]} />
                </>
              )}
            </p>
            <p className="tenue t-num">
              {t('coverage.framesReviewed', {
                frames: chosen.frames,
                reviewed: chosen.reviewed_frames,
                findings: chosen.findings,
              })}
              {chosen.latitude === null && <> · {t('coverage.noCoords')}</>}
            </p>

            {chosen.status === 'declared' ? (
              <>
                <p>
                  {t('coverage.writtenReason', { reason: chosen.not_inspected_reason ?? '' })}
                  {chosen.not_inspected_note !== null && <> · {chosen.not_inspected_note}</>}
                </p>
                <p>
                  <button type="button" onClick={() => declareGap(chosen, false)}>
                    {t('coverage.withdrawGap', { id: chosen.external_element_id })}
                  </button>
                </p>
              </>
            ) : (
              <>
                <h3>{t('coverage.gapTitle')}</h3>
                <label htmlFor="motivo-non-ispezionato">{t('coverage.reason')}</label>
                <select
                  id="motivo-non-ispezionato"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                >
                  {reasons.map((one) => (
                    <option key={one.code} value={one.code}>
                      {one.label}
                    </option>
                  ))}
                </select>
                <label htmlFor="nota-non-ispezionato">{t('coverage.noteOptional')}</label>
                <input
                  id="nota-non-ispezionato"
                  type="text"
                  maxLength={512}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
                <p>
                  <button type="button" disabled={reason === ''} onClick={() => declareGap(chosen, true)}>
                    {t('coverage.declareGap', { id: chosen.external_element_id })}
                  </button>
                </p>
              </>
            )}

            <p className="coverage-links">
              <Link to={`${base}/elements/${chosen.element_id}`}>
                {t('coverage.openSheet', { id: chosen.external_element_id })}
              </Link>
              <Link to={`${base}/map`}>{t('coverage.openMap')}</Link>
              <Link to={`${base}/search?element=${encodeURIComponent(chosen.element_id)}`}>
                {t('coverage.openPhotos')}
              </Link>
            </p>
          </>
        )}
      </aside>
    </div>
  )
}
